import { fetchJSON as fetchAPI } from './client';

export type PlannerDashboard = {
  tenant_id: string;
  active_plans: number;
  pending_approvals: number;
  consolidated_recommendations: number;
  resolved_conflicts: number;
  active_scenarios: number;
  avg_forecast_confidence: number;
  avg_plan_completion_pct: number;
  execution_velocity: number;
  top_objective?: string;
};

export type StrategicPlan = {
  id: string;
  name: string;
  horizon: string;
  objectives?: string[];
  status?: string;
  expected_revenue_impact_pct: number;
  expected_profit_impact_pct: number;
  action_count: number;
  conflict_count: number;
  completion_pct: number;
  requires_approval: boolean;
  source_modules?: string[];
  generated_at: string;
};

export type PlanAction = {
  id: string;
  plan_id: string;
  title: string;
  description?: string;
  source_module: string;
  priority_rank: number;
  revenue_impact_pct: number;
  profit_impact_pct: number;
  risk_score: number;
  effort_score: number;
  cost_estimate: number;
  status?: string;
  requires_approval: boolean;
};

export type ConflictRecord = {
  id: string;
  plan_id?: string;
  conflict_type: string;
  action_a: string;
  action_b: string;
  resolution: string;
  rationale?: string;
  resolved_at: string;
};

export type ScenarioPlan = {
  id: string;
  name: string;
  type: string;
  revenue_impact_pct: number;
  profit_impact_pct: number;
  risk_score: number;
  ad_budget_estimate: number;
  inventory_investment: number;
  ai_credit_estimate: number;
  action_count: number;
};

export type ForecastRecord = {
  id: string;
  plan_id?: string;
  horizon: string;
  revenue_forecast: number;
  profit_forecast: number;
  conversion_forecast_pct: number;
  acos_forecast_pct: number;
  confidence_score: number;
};

export type ConsolidatedRecommendation = {
  id: string;
  source_module: string;
  title: string;
  description?: string;
  objective?: string;
  revenue_impact_pct: number;
  profit_impact_pct: number;
  risk_score: number;
  priority_rank: number;
  conflict_with?: string[];
  included_in_plan: boolean;
};

export type ExecutionProgress = {
  id: string;
  plan_id: string;
  action_id?: string;
  workflow_ref?: string;
  release_ref?: string;
  status: string;
  progress_pct: number;
};

export type ExecutiveBrief = {
  id: string;
  period: string;
  top_opportunities?: string[];
  top_threats?: string[];
  risk_summary?: string;
  revenue_outlook?: string;
  plan_highlights?: string[];
};

export type PlanPerformance = {
  id: string;
  plan_id: string;
  completion_pct: number;
  revenue_achieved_pct: number;
  profit_achieved_pct: number;
  forecast_accuracy_pct: number;
  execution_velocity: number;
  business_impact_score: number;
};

const API_BASE = import.meta.env.VITE_GROWTHPLANNER_API ?? '/api/v1/growthplanner';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const growthplannerApi = {
  dashboard: () => fetchJSON<PlannerDashboard>('/dashboard'),
  listPlans: () => fetchJSON<StrategicPlan[]>('/plans'),
  generatePlan: (body: { horizon: string; objectives?: string[] }) =>
    fetchJSON<StrategicPlan>('/plans/generate', { method: 'POST', body: JSON.stringify(body) }),
  approvePlan: (id: string) =>
    fetchJSON<StrategicPlan>(`/plans/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listActions: (planId?: string) =>
    fetchJSON<PlanAction[]>(`/actions${planId ? `?plan_id=${planId}` : ''}`),
  listConflicts: () => fetchJSON<ConflictRecord[]>('/conflicts'),
  listScenarios: () => fetchJSON<ScenarioPlan[]>('/scenarios'),
  simulateScenario: (type: string) =>
    fetchJSON<ScenarioPlan>('/scenarios/simulate', { method: 'POST', body: JSON.stringify({ type }) }),
  listForecasts: () => fetchJSON<ForecastRecord[]>('/forecasts'),
  listRecommendations: () => fetchJSON<ConsolidatedRecommendation[]>('/recommendations'),
  consolidateRecommendations: () =>
    fetchJSON<ConsolidatedRecommendation[]>('/recommendations/consolidate', { method: 'POST' }),
  listExecution: () => fetchJSON<ExecutionProgress[]>('/execution'),
  executiveBrief: () => fetchJSON<ExecutiveBrief>('/briefing'),
  listPerformance: () => fetchJSON<PlanPerformance[]>('/performance'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
